import { Card } from "@fanduel/og-fe-library";
import { NextSeo } from "next-seo";
import { useEffect, useRef } from "react";
import ErrorComponent from "@/components/empty/ErrorComponent";
import HorseRacingHeader from "@/components/horseRacing/header";
import RaceControls from "@/components/horseRacing/raceControls";
import RaceDetailsTable from "@/components/horseRacing/raceDetailsTable";
import RaceSubtitle from "@/components/horseRacing/raceSubtitle";
import BasicTerms from "@/components/horseRacing/widgets/basicTerms";
import UpcomingRaces from "@/components/horseRacing/widgets/upcomingRaces";
import { RACING_RACE_PAGE_VIEWED } from "@/constants/amplitude";
import { SEO } from "@/constants/seo";
import { TermsT } from "@/types/horseRacing";
import { useAmplitude } from "@/utils/hooks/useAmplitude";
import useRaceByTrack from "@/utils/hooks/useRaceByTrack";
import useTrackRaces from "@/utils/hooks/useTrackRaces";
import styles from "./container.module.scss";

interface HorseRacingContainerProps {
  trackCode: string;
  raceNumber: number;
  tab?: Tab;
  initialTerms: TermsT;
}

type Tab = 1 | 2;

const Container = ({
  trackCode,
  raceNumber,
  initialTerms,
  tab = 1,
}: HorseRacingContainerProps) => {
  const { race, trackName, trackDataSource, isError } = useRaceByTrack(
    trackCode,
    raceNumber
  );
  const { races } = useTrackRaces(trackCode);
  const raceNumberRef = useRef(0);
  const tabRef = useRef<Tab | null>(null);
  const { logAmplitudeEvent } = useAmplitude();

  useEffect(() => {
    if (raceNumber !== raceNumberRef.current || tab !== tabRef.current) {
      if (tab === null || tab === 1) {
        logAmplitudeEvent(RACING_RACE_PAGE_VIEWED, {
          "Track Name": trackName || "unknown",
          "Race Number": raceNumber,
          View: "Full Race Card",
        });
      }
      if (tab === 2) {
        logAmplitudeEvent(RACING_RACE_PAGE_VIEWED, {
          "Track Name": trackName || "unknown",
          "Race Number": raceNumber,
          View: "Consensus Picks",
        });
      }
    }
    raceNumberRef.current = raceNumber;
    tabRef.current = tab;
  }, [raceNumber, logAmplitudeEvent, trackName, tab]);

  const trackNameFallback = trackName ?? "unknown-track";
  const title = `${trackNameFallback} - Race ${raceNumber}`;
  const description = `Check out today's best picks for ${trackName} - Race ${raceNumber}. FanDuel Research provides daily horse racing predictions for all upcoming races!`;
  const canonicalUrl = SEO.getTrackListUrl(
    trackCode,
    trackNameFallback,
    raceNumber
  );

  return (
    <div className={styles.container}>
      <NextSeo
        title={title}
        description={description}
        canonical={canonicalUrl}
      />
      <HorseRacingHeader
        title={title}
        subTitle={
          race ? (
            <div>
              <RaceSubtitle race={race} />
            </div>
          ) : undefined
        }
        postTime={race?.postTime}
      />
      <RaceControls
        races={races}
        verdict={race?.timeform?.analystVerdict ?? undefined}
      />
      <div className={styles.contentContainer}>
        <div>
          {race ? (
            <RaceDetailsTable
              trackCode={trackCode}
              trackName={trackNameFallback}
              race={race}
              tab={tab}
              trackDataSource={trackDataSource}
            />
          ) : (
            <Card
              variant="elevation"
              elevation="mid"
              className={styles.errorContainer}
            >
              <ErrorComponent
                headline={
                  isError ? "Content failed to load" : "No race available"
                }
                subtext={
                  isError ? (
                    <div>
                      <p>No Projections available.</p>
                      <p>Please check back later.</p>
                    </div>
                  ) : (
                    "Please check back later."
                  )
                }
                showCta={isError}
              />
            </Card>
          )}
        </div>
        <div className={styles.widgetContainer}>
          <UpcomingRaces />
          <BasicTerms initialTerms={initialTerms} />
        </div>
      </div>
    </div>
  );
};

export default Container;
